import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

/**
 * Elegant Empty State Component
 * Displays a beautiful, animated empty state with icon, title, description, and optional CTA
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
  actionIcon: ActionIcon,
  variant = "default", // "default" | "minimal" | "card"
  className = "",
  testId = "empty-state"
}) {
  const content = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`flex flex-col items-center justify-center text-center py-12 px-6 ${className}`}
      data-testid={testId}
    >
      {/* Animated Icon Container */}
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, duration: 0.3, type: "spring" }}
        className="relative mb-6"
      >
        {/* Background glow effect */}
        <div className="absolute inset-0 w-24 h-24 rounded-full bg-primary/10 blur-xl" />
        
        {/* Icon circle */}
        <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center">
          {Icon && <Icon className="w-10 h-10 text-primary/60" strokeWidth={1.5} />}
        </div>
        
        {/* Decorative dots */}
        <motion.div
          animate={{ 
            y: [0, -5, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-primary/40"
        />
        <motion.div
          animate={{ 
            y: [0, 5, 0],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          className="absolute -bottom-1 -left-1 w-2 h-2 rounded-full bg-primary/30"
        />
      </motion.div>

      {/* Title */}
      <motion.h3
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-xl font-semibold text-foreground mb-2"
      >
        {title}
      </motion.h3>

      {/* Description */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-muted-foreground max-w-sm mb-6 leading-relaxed"
      >
        {description}
      </motion.p>

      {/* CTA Button */}
      {actionLabel && onAction && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Button
            onClick={onAction}
            className="btn-primary group"
            data-testid={`${testId}-action-btn`}
          >
            {ActionIcon && (
              <ActionIcon className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
            )}
            {actionLabel}
          </Button>
        </motion.div>
      )}
    </motion.div>
  );

  if (variant === "card") {
    return (
      <Card className="border-dashed border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardContent className="p-0">
          {content}
        </CardContent>
      </Card>
    );
  }

  if (variant === "minimal") {
    return (
      <div className="py-8">
        {content}
      </div>
    );
  }

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
      <CardContent className="p-0">
        {content}
      </CardContent>
    </Card>
  );
}

/**
 * Preset Empty States for common scenarios
 */
export const EmptyStatePresets = {
  // Products page - no products
  NoProducts: ({ onAction }) => (
    <EmptyState
      icon={({ className, strokeWidth }) => (
        <svg className={className} strokeWidth={strokeWidth} viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z" />
        </svg>
      )}
      title="Votre collection est vide"
      description="Commencez à créer votre catalogue de vêtements. Ajoutez votre premier article pour le voir apparaître ici."
      actionLabel="Ajouter mon premier article"
      onAction={onAction}
      actionIcon={({ className }) => (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 5v14M5 12h14" />
        </svg>
      )}
      variant="card"
      testId="empty-products"
    />
  ),

  // Customers page - no customers
  NoCustomers: ({ onAction }) => (
    <EmptyState
      icon={({ className, strokeWidth }) => (
        <svg className={className} strokeWidth={strokeWidth} viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      )}
      title="Aucune cliente inscrite"
      description="Vos clientes apparaîtront ici dès qu'elles créeront un compte sur votre vitrine. Partagez le lien de votre boutique !"
      actionLabel="Partager ma vitrine"
      onAction={onAction}
      actionIcon={({ className }) => (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
          <polyline points="16 6 12 2 8 6" />
          <line x1="12" y1="2" x2="12" y2="15" />
        </svg>
      )}
      variant="card"
      testId="empty-customers"
    />
  ),

  // Dashboard - no sales
  NoSales: ({ onAction, periodLabel }) => (
    <EmptyState
      icon={({ className, strokeWidth }) => (
        <svg className={className} strokeWidth={strokeWidth} viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="8" cy="21" r="1" />
          <circle cx="19" cy="21" r="1" />
          <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
        </svg>
      )}
      title="Aucune vente"
      description={periodLabel ? `Aucune vente enregistrée pour ${periodLabel}. C'est le moment idéal pour promouvoir vos articles !` : "Aucune vente enregistrée. Commencez à vendre dès maintenant !"}
      actionLabel="Faire une vente"
      onAction={onAction}
      actionIcon={({ className }) => (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      )}
      variant="minimal"
      testId="empty-sales"
    />
  ),

  // Orders page - no orders
  NoOrders: ({ onAction }) => (
    <EmptyState
      icon={({ className, strokeWidth }) => (
        <svg className={className} strokeWidth={strokeWidth} viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      )}
      title="Aucune commande"
      description="Les commandes de vos clientes apparaîtront ici. Partagez votre vitrine pour recevoir vos premières commandes !"
      actionLabel="Voir ma vitrine"
      onAction={onAction}
      actionIcon={({ className }) => (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
      )}
      variant="card"
      testId="empty-orders"
    />
  ),

  // Search - no results
  NoSearchResults: ({ searchTerm }) => (
    <EmptyState
      icon={({ className, strokeWidth }) => (
        <svg className={className} strokeWidth={strokeWidth} viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      )}
      title="Aucun résultat"
      description={searchTerm ? `Aucun résultat pour "${searchTerm}". Essayez avec d'autres termes.` : "Aucun élément ne correspond à votre recherche."}
      variant="minimal"
      testId="empty-search"
    />
  ),

  // Reviews - no reviews
  NoReviews: ({ onAction }) => (
    <EmptyState
      icon={({ className, strokeWidth }) => (
        <svg className={className} strokeWidth={strokeWidth} viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      )}
      title="Aucun avis"
      description="Les avis de vos clientes apparaîtront ici. Encouragez vos clientes satisfaites à partager leur expérience !"
      variant="card"
      testId="empty-reviews"
    />
  ),
};

export default EmptyState;
